import React, { useEffect } from "react";
import Lottie from "react-lottie";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Col, Row } from "reactstrap";
import { Box, Flex, Image, Title } from "@mantine/core";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import axios from "axios";

import { GreenWaves } from "@/components/GreenWaves";
import { ThemedButton } from "@/components/ThemedButton";

import { resetUser } from "actions/auth";

import styles from "@/views/pages/auth/Login.module.scss";
import { getErrorMessage } from "utilities/error";
import { logGTMScreen, removeLogGTMUser } from "utilities/googleTagManager";

import SDALogo from "@/assets/sda-logo.svg";

import loginAnimation from "./assets/login.json";

const loginAnimationOptions = {
	loop: true,
	autoplay: true,
	animationData: loginAnimation,
	rendererSettings: {
		preserveAspectRatio: "xMidYMid slice",
	},
};

function AuthLayout(props) {
	// make sure search param is not lost, during navigation
	const { mobileAlwaysShown } = props;
	const location = useLocation();

	// force mobile always show the form in slide up mode on following route
	const alwaysShownRoute = [
		"/login/hyperadmin-user-selection",
		"/login/reset-password",
		"/login/reset-password/otp-validation",
	];

	const isAlwaysShownRoute = alwaysShownRoute.includes(location.pathname);

	const search = window.location.search.replace(/^(\?+)/, "");
	const dispatch = useDispatch();
	let navigate = useNavigate();
	const isMobileMode = useMediaQuery("(max-width:991px)");
	// const { upskilling100Flag } = useUpskilling100Flag();

	const { user: currentUser, isLoggedIn } = useSelector((state) => state.auth);

	const [loginFormVisible, setLoginFormVisible] = React.useState(mobileAlwaysShown);
	const shouldSlideUp = mobileAlwaysShown || loginFormVisible || isAlwaysShownRoute;

	useEffect(() => {
		logGTMScreen(location, isLoggedIn, currentUser);
	}, [location]);

	useEffect(() => {
		const UNAUTHORIZED = 401;
		const interceptor = axios.interceptors.response.use(
			(response) => response,
			(error) => {
				const { status } = error.response;
				let error_message = getErrorMessage(error);
				if (status === UNAUTHORIZED && error_message == "Unauthenticated.") {
					removeLogGTMUser();
					dispatch(resetUser());
					localStorage.removeItem("user");
					navigate("/login");
				}
				throw error;
			},
		);

		return () => {
			axios.interceptors.response.eject(interceptor);
		};
	}, []);

	if (isLoggedIn) {
		if (!currentUser.user.email_verified_at && location.pathname != "/login/otp-validation") {
			return <Navigate to={"/login/otp-validation?" + search} />;
		}

		if (!currentUser.user.reset_password && location.pathname == "/login") {
			return <Navigate to={"/?" + search} />;
		}

		if (location.pathname == "/login/register") {
			return <Navigate to={"/?" + search} />;
		}
	}

	const mobileFloatingOffset = 250;

	return (
		<div className="main-content" style={{ overflowY: "hidden" }}>
			<Row style={{ marginLeft: 0, marginRight: 0 }} className={styles.login_wrap}>
				{isMobileMode ? (
					<div
						style={{
							position: "absolute",
							flexDirection: "column",
							display: "flex",
							// top: shouldSlideUp? "calc(5vh)":"calc()" ,
							paddingTop: 20,
							bottom: shouldSlideUp ? "0" : `calc(-100vh + ${mobileFloatingOffset}px)`,
							backgroundColor: shouldSlideUp ? "rgba(0,0,0,0.3)" : "transparent",
							transition: "all 0.5s",
							height: "100vh",
							width: "100%",
							zIndex: 10,
						}}>
						<div
							style={{
								marginTop: 20,
								paddingTop: 25,
								borderTopLeftRadius: "20px",
								borderTopRightRadius: "20px",
								flexGrow: 1,
								backgroundColor: "white",
								flexShrink: 1,
								overflowY: shouldSlideUp ? "auto" : "hidden",
								paddingRight:
									"17px" /* Increase/decrease this value for cross-browser compatibility */,
								boxSizing: "content-box" /* So the width will be 100% + 17px */,
								paddingBottom: 100,
								boxShadow: "0px 0px 10px 0px rgba(0,0,0,0.3)",
							}}>
							<Outlet
								context={{
									isSlideUp: shouldSlideUp,
									signButton: !shouldSlideUp && (
										<div className="d-flex justify-content-center">
											{/* <Button
												size='lg'
												style={{
													width: 200,
												}}
												className={classnames("btn-gradient", styles.submit_button)}
												onClick={() => {
													if (mobileAlwaysShown) {
														return;
													}
													setLoginFormVisible(!shouldSlideUp);
												}}>
												{shouldSlideUp ? "Close" : "Sign in"}
											</Button> 
											*/}
											<ThemedButton
												onClick={() => {
													if (mobileAlwaysShown) {
														return;
													}
													setLoginFormVisible(!shouldSlideUp);
												}}>
												{shouldSlideUp ? "Close" : "Sign in"}
											</ThemedButton>
										</div>
									),
								}}
							/>
						</div>
					</div>
				) : (
					<Col lg="6" md="12" className={`${styles.login_right_col}  order-sm-last`}>
						<div className={styles.wrap}>
							<Outlet />
						</div>
					</Col>
				)}
				<Col className={`${styles.login_left_col} px-0`} lg="6" md="12">
					<Box pos={"relative"} w="100%" h="100%">
						<GreenWaves w="100%" />
						<Image
							src={SDALogo}
							pos={"absolute"}
							alt="FCA Logo"
							w={200}
							h={"auto"}
							fit="contain"
							right={20}
							top={30}
						/>
						<Flex
							visibleFrom="md"
							w="100%"
							pos={"absolute"}
							left={20}
							bottom={50}
							align={"end"}
							mb={30}
							gap={20}
							justify={"space-between"}>
							<Title c="white" fz={35}>
								SKILLS FOR EVERYONE
							</Title>
						</Flex>
					</Box>
				</Col>
			</Row>
		</div>
	);
}

export default AuthLayout;
